/**
 * useLifeReportJob — KALKI 7.0 single source of truth for a Life Report
 * generation job.
 *
 * Why a hook (not a context):
 *   • The job is a server-owned durable artefact — the frontend only tracks
 *     a `jobId` + the latest polled snapshot.  Any page that mounts this hook
 *     resumes polling the SAME job without duplicating network work.
 *   • `jobId` is persisted in localStorage keyed by `user_id` so a page
 *     refresh / tab close / navigation / device switch picks up the exact
 *     same job once the user signs in again.
 *   • Fires a `kalki:report-ready` CustomEvent on COMPLETED so the global
 *     header can refresh the "Download Latest Report" item + the resume
 *     toast can dismiss itself.
 *
 * Public API returned to the caller:
 *   {
 *     job,                   // JobOut snapshot or null
 *     status,                // one of JOB_STATES / null
 *     progress,              // 0-100 (stage-based)
 *     stageLabel,            // human string from backend
 *     reportId,              // populated when COMPLETED
 *     error,                 // populated when FAILED
 *     elapsedSeconds,        // server-synced elapsed since created_at
 *     targetSeconds,         // typically 600
 *     remainingSeconds,      // target - elapsed, floored at 0
 *     isTerminal,            // COMPLETED | FAILED | REFUNDED
 *     start({ idempotency_key, draft_id }),  // POST /reports/generate_async
 *                                            // persists jobId, kicks polling
 *     attach(jobId),          // adopt an existing jobId (resume path)
 *     dismiss(),              // clear localStorage (e.g. after View Report)
 *   }
 *
 * The hook keeps a module-level singleton `__POLL__` so two components can
 * mount it concurrently without creating two HTTP pollers for the same job.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, useAuth } from "@/hooks/useAuth";

const LS_KEY = (uid) => `kalki:lifeReportJob:${uid || "anon"}`;
const POLL_MS = 1800;
const TERMINAL = new Set(["COMPLETED", "FAILED", "REFUNDED"]);

// Module-level singleton: at most ONE poller per (user, job).
let __POLL__ = { key: null, timer: null, listeners: new Set(), snapshot: null };

function readPersisted(uid) {
  try {
    const raw = localStorage.getItem(LS_KEY(uid));
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || !p.job_id) return null;
    return p;
  } catch (_) { return null; }
}

function writePersisted(uid, job) {
  try {
    localStorage.setItem(LS_KEY(uid), JSON.stringify({
      job_id: job.job_id,
      status: job.status,
      report_id: job.report_id || null,
      started_at: job.created_at,
      target_seconds: job.target_seconds || 600,
    }));
  } catch (_) {}
}

function clearPersisted(uid) {
  try { localStorage.removeItem(LS_KEY(uid)); } catch (_) {}
}

function broadcast(snap) {
  __POLL__.snapshot = snap;
  __POLL__.listeners.forEach((fn) => { try { fn(snap); } catch (_) {} });
}

async function pollOnce(jobId) {
  try {
    const { data } = await api.get(`/reports/jobs/${jobId}`);
    return data;
  } catch (e) {
    // 404 → job was never created or purged; treat as missing.
    const code = e?.response?.status;
    if (code === 404) return { job_id: jobId, status: "MISSING", error: "Job not found" };
    return null;
  }
}

function startLoop(jobId, uid) {
  // Already polling this exact job — do nothing.
  if (__POLL__.key === jobId && __POLL__.timer) return;
  // Replace any previous poller.
  if (__POLL__.timer) clearTimeout(__POLL__.timer);
  __POLL__.key = jobId;
  const tick = async () => {
    const snap = await pollOnce(jobId);
    if (snap) {
      broadcast(snap);
      if (uid) {
        // Keep the persisted record fresh (status, report_id) so a
        // separate mount can pick up without an extra round-trip.
        writePersisted(uid, {
          job_id: jobId,
          status: snap.status,
          report_id: snap.report_id,
          created_at: snap.created_at,
          target_seconds: snap.target_seconds || 600,
        });
      }
      if (TERMINAL.has(snap.status)) {
        if (snap.status === "COMPLETED") {
          try { window.dispatchEvent(new CustomEvent("kalki:report-ready",
                 { detail: { report_id: snap.report_id, job_id: jobId } })); }
          catch (_) {}
        }
        // Keep the snapshot so UIs can show the "ready" chip for one
        // render tick, then stop polling.
        __POLL__.timer = null;
        return;
      }
    }
    __POLL__.timer = setTimeout(tick, POLL_MS);
  };
  // Prime once immediately so the UI sees the first snapshot instantly.
  tick();
}

export default function useLifeReportJob() {
  const { user } = useAuth() || {};
  const uid = user?.user_id || user?.id || user?.email || null;
  const [snapshot, setSnapshot] = useState(() => __POLL__.snapshot || null);
  const cbRef = useRef(null);

  // Subscribe this component to the singleton.
  useEffect(() => {
    const cb = (snap) => setSnapshot(snap);
    cbRef.current = cb;
    __POLL__.listeners.add(cb);
    return () => { __POLL__.listeners.delete(cb); };
  }, []);

  // On mount, resume any persisted non-terminal job.
  useEffect(() => {
    if (!uid) return;
    const p = readPersisted(uid);
    if (!p) return;
    // Skip resume if persisted record is already terminal — no need to poll.
    if (TERMINAL.has(p.status) && p.status !== "COMPLETED") {
      // keep snapshot visible for one tick so UI can show failed state
      setSnapshot({ job_id: p.job_id, status: p.status, progress: 100 });
      return;
    }
    // Even COMPLETED jobs need one hydrate call so we have report_id ready.
    startLoop(p.job_id, uid);
  }, [uid]);

  const start = useCallback(async ({ idempotency_key, draft_id }) => {
    if (!idempotency_key) throw new Error("idempotency_key is required");
    const { data } = await api.post("/reports/generate_async", {
      idempotency_key,
      draft_id: draft_id || null,
    });
    if (!data?.job_id) throw new Error("No job_id returned");
    writePersisted(uid, data);
    broadcast(data);
    startLoop(data.job_id, uid);
    return data;
  }, [uid]);

  const attach = useCallback((jobId) => {
    if (!jobId) return;
    startLoop(jobId, uid);
  }, [uid]);

  const dismiss = useCallback(() => {
    if (__POLL__.timer) { clearTimeout(__POLL__.timer); __POLL__.timer = null; }
    __POLL__.key = null;
    __POLL__.snapshot = null;
    broadcast(null);
    clearPersisted(uid);
  }, [uid]);

  // Derive timing from server-authoritative `created_at`.  Even if the user
  // left the tab and came back 20 minutes later, this stays correct.
  let elapsedSeconds = snapshot?.elapsed_seconds ?? 0;
  const targetSeconds = snapshot?.target_seconds ?? 600;
  if (snapshot?.created_at && !snapshot?.completed_at) {
    try {
      const started = new Date(snapshot.created_at).getTime();
      elapsedSeconds = Math.max(0, Math.floor((Date.now() - started) / 1000));
    } catch (_) {}
  }
  const remainingSeconds = Math.max(0, targetSeconds - elapsedSeconds);

  return {
    job: snapshot,
    status: snapshot?.status ?? null,
    progress: snapshot?.progress ?? 0,
    stageLabel: snapshot?.stage ?? "",
    reportId: snapshot?.report_id ?? null,
    error: snapshot?.error ?? null,
    stagesCompleted: snapshot?.stages_completed || [],
    elapsedSeconds,
    targetSeconds,
    remainingSeconds,
    isTerminal: snapshot?.status ? TERMINAL.has(snapshot.status) : false,
    start,
    attach,
    dismiss,
  };
}

export const __test_only__ = { readPersisted, writePersisted, clearPersisted, LS_KEY, TERMINAL };
